/**
 * useBlockedStoragesMain - Hook principal para gestionar la vista de storages bloqueados
 *
 * Orquesta todos los aspectos de la gestión de storages bloqueados:
 * - Carga de datos paginados con infinite scroll
 * - Gestión de filtros avanzados y búsqueda (sincronizados con URL)
 * - Sorting de columnas
 *
 * @returns {Object} Estado completo de la vista con datos, handlers, filtros y configuración
 */

import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { BlockedStorage, EligibleStorageFilters } from '../models/EligibleStorage';
import { useBlockedStorages } from './useBlockedStorages';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  storage_id: 'storage_id',
  product_id: 'product_id',
  storage_block_reason_id: 'storage_block_reason_id',
  user_id: 'user_id',
  created_at: 'created_at',
  comment: 'comment',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) return [];
  return value as FilterConditionOutput[];
};

export const useBlockedStoragesMain = () => {
  const { t } = useTranslation('eligibleStorageModule');

  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: '' as string },
    sortDirection: { type: 'string', default: '' as string },
  });

  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(() =>
    parseFiltersFromParams(queryParams.filters),
  );
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(filterConditions));
  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      return { field: 'id', direction: 'desc' };
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';
    return {
      field: sortField,
      direction,
    };
  });

  // Sincronización con query params
  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);

    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      setSorting((prev) =>
        prev?.field === 'id' && prev?.direction === 'desc' ? prev : { field: 'id', direction: 'desc' },
      );
      return;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';

    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // Estados de columnas visibles - mismas que elegibles más las de bloqueo
  const tableColumns = [
    { accessorKey: 'product', header: t('table.product') },
    { accessorKey: 'client', header: t('table.client') },
    { accessorKey: 'storage_date', header: t('table.date') },
    { accessorKey: 'dimensions', header: t('table.dimensions') },
    { accessorKey: 'quantity', header: t('table.units') },
    { accessorKey: 'unit_volume_m3', header: t('table.unitVolume') },
    { accessorKey: 'total_volume_m3', header: t('table.totalVolume') },
    { accessorKey: 'period', header: t('table.period') },
    { accessorKey: 'applied_rate', header: t('table.appliedRate') },
    { accessorKey: 'base_amount', header: t('table.baseAmount') },
    { accessorKey: 'total_amount', header: t('table.totalAmount') },
    { accessorKey: 'financialStatus', header: t('table.financialStatus') },
    { accessorKey: 'preinvoice_id', header: t('table.preinvoiceNumber') },
    { accessorKey: 'invoice_id', header: t('table.invoiceNumber') },
    { accessorKey: 'block_reason', header: t('table.block_reason', 'Motivo de bloqueo') },
    { accessorKey: 'block_comment', header: t('table.block_comment', 'Comentario') },
    { accessorKey: 'blocked_by', header: t('table.blocked_by', 'Bloqueado por') },
    { accessorKey: 'created_at', header: t('table.created_at', 'Fecha de bloqueo') },
    { accessorKey: 'actions', header: '' }, // Columna de acciones siempre visible
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.accessorKey));

  // Procesar filtros
  const processedFilters = useMemo(() => {
    try {
      const searchFilterFromConditions =
        filterConditions.length > 0
          ? {
              filters: filterConditions.map((condition) => {
                const requiresArray = ['in', 'not_in'].includes(condition.action);
                const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

                const value = isNullOperator
                  ? undefined
                  : requiresArray
                    ? Array.isArray(condition.value)
                      ? condition.value
                      : [condition.value]
                    : condition.value;

                return {
                  column: condition.column,
                  operator: condition.action,
                  ...(value !== undefined && { value: value }),
                };
              }),
              logic: 'AND',
            }
          : undefined;

      // Mapear el campo de sorting a los parámetros de la API
      const apiField = sorting?.field ? fieldToApiMapping[sorting.field] || 'id' : 'id';
      const apiDirection = sorting?.direction || 'desc';

      return {
        sortBy: apiField,
        sortDirection: apiDirection,
        ...(searchFilterFromConditions && {
          searchFilter: searchFilterFromConditions,
        }),
      };
    } catch (error) {
      return {};
    }
  }, [filterConditions, sorting]);

  // Memoizar el objeto de filtros para evitar re-renders infinitos
  const queryFilters = useMemo(
    () => ({
      search: searchTerm,
      ...processedFilters,
      sorting,
    }),
    [searchTerm, processedFilters, sorting],
  );

  // Hooks de datos
  const {
    blockedStorages,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    total,
  } = useBlockedStorages(queryFilters);

  // Handlers
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);
      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParams({
        filters: conditions,
      });
    },
    [updateParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      updateParam('search', value);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);
      if (newSorting) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  const hasActiveFilters = useMemo(
    () => filterConditions.length > 0 || (searchTerm?.trim() ?? '').length > 0,
    [filterConditions, searchTerm],
  );

  return {
    // Estados
    selectedColumns,
    sorting,

    // Datos
    blockedStorages,
    isLoading,
    isFetching,
    error,
    total,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
  };
};
